<script setup lang="ts">
import { useTextareaAutosize } from "@vueuse/core";
import { computed, onMounted, ref, watch } from "vue";

import actions from "~/actions";
import { getPropertyDisplayTitle } from "~/common/properties";
import { EditorMode, PropertyKind } from "~/shared/enums";
import type { Property } from "~/shared/types";
import { usePageStore } from "~/stores";
import { when } from "~/utils/wait";

const props = defineProps<{
  property: Property;
  value?: string;
  editorMode: EditorMode;
}>();

const emit = defineEmits<{
  update: [value: string];
}>();

const pageStore = usePageStore();

const textInner = ref(props.value ?? "");
const { textarea } = useTextareaAutosize({ input: textInner });

const isFormMode = computed(() => props.editorMode === EditorMode.FORM);
const isAutomationMode = computed(() => props.editorMode === EditorMode.AUTOMATION);
const placeholder = computed(() =>
  isAutomationMode.value ? "(Unset)" : props.property.kind === PropertyKind.DEFAULT_TITLE ? "Write a task" : "Add text"
);

watch(
  () => props.value,
  (text) => {
    const newText = text ?? "";
    if (newText === textInner.value) {
      return;
    }

    textInner.value = newText;
  }
);

const updateOnInput = () => {
  if (isAutomationMode.value) {
    return;
  }
  emit("update", textInner.value);
};

const updateText = () => {
  emit("update", textInner.value);
};

const onEnterPress = () => textarea.value?.blur();

const focus = () => {
  textarea.value?.focus();
};

const onClick = async () => {
  if (!isAutomationMode.value || !pageStore.isMobile) {
    return;
  }
  actions.visualization.openKeyboardIfIos();
  await when(textarea);
  textarea.value?.focus();
};

onMounted(async () => {
  if (isAutomationMode.value) {
    actions.visualization.openKeyboardIfIos();
    await when(textarea);
    focus();
  }
});

defineExpose({
  focus,
});
</script>

<template>
  <textarea
    ref="textarea"
    v-model="textInner"
    :aria-label="getPropertyDisplayTitle(property)"
    :placeholder="placeholder"
    class="min-h-8 w-full break-words cursor-text resize-none rounded-sm border bg-std px-2 py-[5px] text-sm hyphens-auto text-md caret-md focus-ring-none select-none placeholder:text-vlt focus:border-primary-base dark:focus-ring-none dark:focus:border-primary-base"
    :class="{
      'border-0': isAutomationMode,
      'border-md hover:border-hvy': isFormMode,
    }"
    @click.stop="onClick"
    @keydown.esc.stop.prevent="textarea?.blur()"
    @keydown.enter.stop.prevent="onEnterPress"
    @paste="updateText"
    @blur="updateText"
    @input="updateOnInput" />
</template>
